/**
 * The palette, as TypeScript.
 *
 * WHY THIS FILE EXISTS AS WELL AS `@theme` IN globals.css:
 * the generated SVG placeholders (project covers, backdrop, portrait) are serialised
 * into `data:` URIs and handed to an `<img>`. An SVG loaded as an image is an isolated
 * document — it cannot read the host page's CSS custom properties. So those generators
 * need literal hex values in JS, which means the palette has to exist twice.
 *
 * This file is the SOURCE. `src/styles/globals.css` mirrors it under `@theme`.
 * `tokens.test.ts` parses that CSS and fails if the two ever drift apart. Do not
 * edit one without the other.
 *
 * Values come from context/project-overview.md § UI/UX → Colour palette, with two
 * corrections recorded in context/research/scaffold-decisions.md:
 *   - `faint` is #847F76, not the spec's #7C776F (which measures 4.45:1 against the
 *     page background and fails the 4.5:1 minimum the spec claims it clears).
 *   - `prod` is added; the spec's table omits a colour for ProjectStatus.InProduction
 *     even though the status enum has three values and the cover generator keys off it.
 */

export const COLORS = {
  bg: '#0A0A0B',
  surface: '#0F0F11',
  panel: '#141416',
  line: '#232327',
  lineStrong: '#313137',
  text: '#EAE7E1',
  muted: '#96928A',
  faint: '#847F76',
  accent: '#E0A458',
  accentDeep: '#C97B4E',
  accentHover: '#F0C07A',
  live: '#74BE97',
  prod: '#E0A458',
  dev: '#8FA3B8',
  danger: '#E4796F',
} as const;

/**
 * Translucent surfaces the prototype uses that the spec's 14-token table omits.
 * Declared here so no component ever inlines an rgba(). See scaffold-decisions.md.
 */
export const SURFACES = {
  /** Accent fill at 10% — tag backgrounds, hover washes. */
  accentSoft: 'rgba(224, 164, 88, 0.10)',
  /** Accent border at 28% — tag and card outlines. */
  accentLine: 'rgba(224, 164, 88, 0.28)',
  /** Row / card hover fill. */
  hover: 'rgba(35, 35, 39, 0.55)',
  /** 1px top inset highlight on raised rows. */
  highlight: 'rgba(234, 231, 225, 0.06)',
  /** Media placeholder ground, behind covers and the portrait. */
  well: '#101012',
} as const;

/**
 * The split-layout breakpoint, in CSS pixels.
 *
 * DECLARED IN PIXELS, DELIBERATELY. Tailwind v4's default breakpoints are in `rem`,
 * and `rem` inside a media query resolves against the BROWSER's default font size,
 * not the page's html font-size. A visitor whose browser default is 20px would get a
 * `53.75rem` breakpoint firing at 1075 CSS px.
 *
 * That is not cosmetic here. This same number decides, in JS, whether the
 * IntersectionObserver root is the right-hand pane or `null`, and whether scrollspy
 * measures against the pane or the window. If the CSS switches to two columns at a
 * different width than the JS thinks it does, then between those two widths you get
 * the stacked layout with a pane-rooted observer — the root is a full-height element
 * that never scrolls, nothing intersects, every `.reveal` stays at opacity 0, and the
 * page is blank. Silently, and only at certain widths and font settings.
 *
 * One number, two consumers: `--breakpoint-split` in globals.css and matchMedia here.
 */
export const SPLIT_BREAKPOINT_PX = 860;

/** The media query string for the split layout. Use this, never a literal. */
export const SPLIT_MEDIA_QUERY = `(min-width: ${SPLIT_BREAKPOINT_PX}px)`;

/** Motion, from § UI/UX → Animation. */
export const MOTION = {
  /** Reveal and nav easing. */
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** Scroll reveal duration. */
  revealMs: 800,
  /** Rise distance for a scroll reveal, in px. */
  revealRisePx: 20,
  /** Stagger between successive items in a revealed list. */
  revealStaggerMs: 80,
  /**
   * Cap on staggered items. Past this the delay stops growing, so a long list does
   * not leave its tail invisible for seconds.
   */
  revealStaggerCap: 12,
} as const;

export type ColorToken = keyof typeof COLORS;
